import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { loadAds, track } from "@/lib/ads";
import { readConsent } from "@/lib/consent";
import { rememberEmail, saveBirthday } from "@/lib/knownEmail";
import { BirthdayField } from "@/components/site/BirthdayField";

const SEEN = "ozy_welcome_seen";
const REF = "ozy_parrain";
const HIDDEN = /^\/(admin|commande|payment|espace-ambassadeur|mon-abonnement|abonnement)/;

/** Capture ?parrain=CODE (pré-rempli au paiement) et affiche une seule fois, après 20 s, l'offre -10 % contre l'email. */
export function WelcomePopup() {
  const { pathname, search } = useLocation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [birthday, setBirthday] = useState({ day: "", month: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => { window.__ozyLoadAds = loadAds; loadAds(); return undefined; }, []);
  useEffect(() => { track("PageView"); }, [pathname]);
  useEffect(() => {
    const code = new URLSearchParams(search).get("parrain");
    if (code && /^[A-Z0-9_-]{4,30}$/i.test(code)) localStorage.setItem(REF, code.toUpperCase());
  }, [search]);
  useEffect(() => {
    if (localStorage.getItem(SEEN) || localStorage.getItem(REF) || HIDDEN.test(pathname)) return undefined;
    let t;
    const show = () => {
      if (!readConsent()) { t = setTimeout(show, 5000); return; } // attendre la réponse au bandeau cookies (pas deux fenêtres superposées)
      setOpen(true);
      localStorage.setItem(SEEN, "1");
    };
    t = setTimeout(show, 20000);
    return () => clearTimeout(t);
  }, [pathname]);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/welcome/subscribe", { email: email.trim(), consent });
      rememberEmail(email);
      saveBirthday(email, birthday.day, birthday.month, "popup_bienvenue");
      if (data?.status === "sent") track("Lead");
      setMsg(data?.status === "customer" ? "Vous êtes déjà cliente : merci ! L'offre de bienvenue est réservée aux premières commandes." : data?.status === "already" ? "Vous êtes déjà inscrite : votre code vous a été envoyé par email." : "C'est envoyé ! Votre code -10 % arrive dans votre boîte email (pensez aux courriers indésirables).");
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  if (!open) return null;
  return (
    <div data-testid="welcome-popup" role="dialog" aria-modal="true" className="fixed inset-0 z-[80] flex items-end justify-center bg-black/60 p-4 backdrop-blur-sm sm:items-center">
      <div className="relative w-full max-w-md rounded-sm border border-[var(--gold)]/60 bg-[var(--ink)] p-7">
        <button data-testid="welcome-popup-close" onClick={() => setOpen(false)} aria-label="Fermer" className="absolute right-3 top-3 p-2 text-[var(--parchment)]/80 hover:text-[var(--gold-soft)]"><X className="h-5 w-5" /></button>
        <p className="eyebrow mb-3">Bienvenue chez Ozentya</p>
        <p className="font-display text-3xl leading-tight"><span className="italic gold-text">-10 %</span> sur votre 1re commande</p>
        {msg ? <p data-testid="welcome-popup-done" className="mt-5 text-sm text-[var(--parchment)]/90">{msg}</p> : (
          <form onSubmit={send} className="mt-5 space-y-3">
            <input data-testid="welcome-popup-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="votre@email.fr" autoComplete="email"
              className="h-12 w-full rounded-sm border border-[var(--line)] bg-[var(--ink-2)] px-3 text-base outline-none focus:border-[var(--gold)]" />
            <BirthdayField value={birthday} onChange={setBirthday} testid="welcome-popup-birthday" />
            <label className="flex items-start gap-2 text-xs text-[var(--parchment)]/85"><input data-testid="welcome-popup-consent" type="checkbox" required checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />J'accepte de recevoir les offres et conseils Ozentya par email (désinscription en un clic).</label>
            <button data-testid="welcome-popup-submit" disabled={busy} className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[var(--gold)] text-xs font-semibold uppercase tracking-[0.18em] text-[var(--ink)] hover:bg-[var(--gold-soft)] disabled:opacity-40">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Recevoir mon code</button>
          </form>
        )}
      </div>
    </div>
  );
}

export const referralCode = () => { try { return localStorage.getItem(REF) || ""; } catch { return ""; } };
